/**
 * product-form.js — Mavin Parfum
 * Gallery navigation + quantity stepper + variant selection.
 */

class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.main = this.querySelector('[data-gallery-main]');
    this.thumbs = this.querySelectorAll('[data-gallery-thumb]');

    this.thumbs.forEach(t => t.addEventListener('click', e => {
      e.preventDefault();
      this.setActive(t);
    }));
  }

  setActive(thumb) {
    const src = thumb.dataset.src;
    const alt = thumb.dataset.alt || '';
    this.setMainImage(src, alt);
    this.thumbs.forEach(t => t.classList.toggle('is-active', t === thumb));
  }

  setMainImage(src, alt = '') {
    const img = this.main.querySelector('img');
    if (!img || !src) return;

    img.removeAttribute('srcset');
    img.removeAttribute('sizes');

    try {
      const url = new URL(src, window.location.origin);
      url.searchParams.set('width', '1200');
      img.src = url.toString();
    } catch (e) {
      img.src = src;
    }

    if (alt) img.alt = alt;
  }

  setActiveByVariantImage(image) {
    if (!image) return;

    const src = image.src || image.preview_image?.src;
    if (!src) return;

    this.setMainImage(src, image.alt || '');

    const clean = src.split('?')[0];
    const filename = clean.split('/').pop();

    this.thumbs.forEach(t => {
      const thumbSrc = (t.dataset.src || '').split('?')[0];
      const isMatch = thumbSrc === clean || (filename && thumbSrc.includes(filename));
      t.classList.toggle('is-active', isMatch);
    });
  }
}

customElements.define('product-gallery', ProductGallery);

class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="number"]');

    this.querySelectorAll('[data-qty-change]').forEach(btn => {
      btn.addEventListener('click', () => {
        const delta = parseInt(btn.dataset.qtyChange, 10);
        const val = Math.max(1, (parseInt(this.input.value, 10) || 1) + delta);
        this.input.value = val;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }
}

customElements.define('quantity-input', QuantityInput);

class VariantPicker extends HTMLElement {
  connectedCallback() {
    const jsonEl = this.querySelector('script[data-variants]');

    try {
      this.variants = jsonEl ? JSON.parse(jsonEl.textContent) : [];
    } catch (e) {
      this.variants = [];
    }

    this.selects = Array.from(this.querySelectorAll('[data-variant-option]'));
    this.idInput = this.querySelector('[data-variant-id]');
    this.submitBtn = this.querySelector('[data-add-to-cart]');
    this.btnLabel = this.querySelector('[data-btn-label]');

    if (this.btnLabel && !this.btnLabel.dataset.addLabel) {
      this.btnLabel.dataset.addLabel = this.btnLabel.textContent.trim();
    }

    this.selects.forEach(s => s.addEventListener('change', () => this.onChange()));

    if (this.variants.length > 0) {
      this.onChange();
    }
  }

  onChange() {
    const chosen = this.selects.map(s => s.value);
    const match = this.variants.find(v => v.options.every((opt, i) => opt === chosen[i]));

    if (!match) {
      this.setAvailableAnyway();
      return;
    }

    this.updateVariant(match);
  }

  setAvailableAnyway() {
    if (this.submitBtn) {
      this.submitBtn.removeAttribute('disabled');
      this.submitBtn.disabled = false;
    }

    if (this.btnLabel) {
      this.btnLabel.textContent = this.btnLabel.dataset.addLabel || 'Aggiungi al carrello';
    }
  }

  updateVariant(variant) {
    if (this.idInput) this.idInput.value = variant.id;

    try {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    } catch (e) {}

    /*
      FIX MAVIN:
      Il tema non deve mostrare "Esaurito".
      Tutte le varianti devono restare acquistabili.
    */
    if (this.submitBtn) {
      this.submitBtn.removeAttribute('disabled');
      this.submitBtn.disabled = false;

      if (this.btnLabel) {
        this.btnLabel.textContent = this.btnLabel.dataset.addLabel || 'Aggiungi al carrello';
      }
    }

    this.renderPrice(variant);
    this.updateSku(variant);
    this.updateVariantImage(variant);

    this.dispatchEvent(new CustomEvent('variant:change', { detail: variant, bubbles: true }));
  }

  updateSku(variant) {
    const skuEl = document.querySelector('[data-sku-target]');
    if (skuEl && variant.sku) {
      skuEl.textContent = 'CODICE · ' + variant.sku;
    }
  }

  updateVariantImage(variant) {
    const image = variant.featured_media?.preview_image || variant.featured_media || variant.featured_image;
    if (!image) return;

    const gallery = document.querySelector('product-gallery');

    if (gallery && typeof gallery.setActiveByVariantImage === 'function') {
      gallery.setActiveByVariantImage(image);
      return;
    }

    const mainImg = document.querySelector('[data-gallery-main] img');
    const src = image.src || image.preview_image?.src;

    if (mainImg && src) {
      mainImg.removeAttribute('srcset');
      mainImg.removeAttribute('sizes');

      try {
        const url = new URL(src, window.location.origin);
        url.searchParams.set('width', '1200');
        mainImg.src = url.toString();
      } catch (e) {
        mainImg.src = src;
      }

      if (image.alt) mainImg.alt = image.alt;
    }
  }

  renderPrice(variant) {
    const target = document.querySelector('[data-price-target]');
    if (!target) return;

    const fmt = this.formatter();
    const priceStr = fmt.format(variant.price / 100);
    let html = `<span class="price">${priceStr}</span>`;

    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      const compareStr = fmt.format(variant.compare_at_price / 100);
      html += ` <span class="price--compare">${compareStr}</span>`;
    }

    target.innerHTML = html;
  }

  formatter() {
    if (this._fmt) return this._fmt;

    const locale = document.documentElement.lang || 'it-IT';
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';

    try {
      this._fmt = new Intl.NumberFormat(locale, { style: 'currency', currency });
    } catch (e) {
      this._fmt = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' });
    }

    return this._fmt;
  }
}

customElements.define('variant-picker', VariantPicker);

class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.dataset.loaded === 'true') return;
    if (!this.dataset.productId) return;

    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries, obs) => {
        entries.forEach(e => {
          if (e.isIntersecting) {
            this.loadRecs();
            obs.unobserve(this);
          }
        });
      }, { rootMargin: '400px 0px' });
      io.observe(this);
    } else {
      this.loadRecs();
    }
  }

  async loadRecs() {
    const { sectionId, productId, intent, limit } = this.dataset;

    const params = new URLSearchParams({
      section_id: sectionId,
      product_id: productId,
      intent: intent || 'related',
      limit: limit || '4'
    });

    const url = `${window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/'}recommendations/products?${params}`;

    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error('Rec fetch failed');

      const html = await res.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const incoming = parsed.querySelector('product-recommendations');

      if (!incoming) {
        this.hideSection();
        return;
      }

      const newGrid = incoming.querySelector('.mavin-pdp-rec__grid:not([data-skeleton])');
      const currentContainer = this.querySelector('.container');
      const currentGrid = this.querySelector('.mavin-pdp-rec__grid[data-skeleton]');

      if (newGrid && newGrid.children.length > 0) {
        if (currentGrid) currentGrid.replaceWith(newGrid);
        else if (currentContainer) currentContainer.appendChild(newGrid);
        this.dataset.loaded = 'true';
      } else {
        this.hideSection();
      }
    } catch (err) {
      console.warn('[recommendations]', err);
      this.hideSection();
    }
  }

  hideSection() {
    this.style.display = 'none';
  }
}

customElements.define('product-recommendations', ProductRecommendations);